
/**
 * Formats a creator's name into initials
 * @param name Full name of the creator
 * @returns Initials (e.g., "JS" for "John Smith")
 */
export const getCreatorInitials = (name: string): string => {
  if (!name) return '?';
  
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Formats a number for display
 * - For large numbers: converts to K (thousands) or M (millions)
 * - For small numbers: returns as is, ensuring a minimum of "1" for non-zero values
 * @param count The number to format
 * @returns Formatted number as a string
 */
export const formatNumber = (count: number | undefined): string => {
  if (count === undefined || count === null) return "0";
  
  // Always show at least "1" for non-zero values that round to zero
  if (count > 0 && count < 1) return "1";
  
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  } else if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  
  // Return as integer for cleaner display
  return Math.round(count).toString();
};

/**
 * Formats a duration in seconds to a readable time format
 * @param seconds Duration in seconds
 * @returns Formatted time string (e.g., "1:23" or "1:23:45")
 */
export const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return "0:00";
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  
  // Format with leading zeros for seconds
  const formattedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : `${remainingSeconds}`;
  
  // Include hours only if present
  if (hours > 0) {
    const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    return `${hours}:${formattedMinutes}:${formattedSeconds}`;
  }
  
  return `${minutes}:${formattedSeconds}`;
};
